// src/products/dataroom/state/dataroomStore.js
// Centralized Zustand store for all dataroom state management

import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { supabase } from '../../../config';
import partyTeamService from '../services/partyTeamService';
import wizardUsersService from '../services/wizardUsersService';
import projectService from '../services/projectService';
import permissionService from '../services/permissionService';

const useDataroomStore = create(
  subscribeWithSelector((set, get) => ({
    // =============================================
    // CORE STATE
    // =============================================
    
    // Projects
    projects: [],
    selectedProject: null,
    selectedProjectId: null,
    
    // Items (files/folders)
    items: [],
    treeData: [],
    currentFolder: null,
    
    // Users
    users: [],
    userRoles: {},
    
    // Parties & Teams
    parties: [],
    teams: {},
    expandedParties: {},
    
    // UI State
    modals: {},
    breadcrumb: [],
    searchTerm: '',
    messages: [],
    
    // Loading States
    loading: {
      projects: false,
      items: false,
      users: false,
      parties: false,
      teams: false,
      global: false
    },
    
    // Error States
    errors: {
      projects: null,
      items: null,
      users: null,
      parties: null,
      teams: null,
      global: null
    },

    // =============================================
    // PROJECT MANAGEMENT ACTIONS
    // =============================================
    
    setProjects: (projects) => set({ projects }),
    
    setSelectedProject: (project) => set({ 
      selectedProject: project,
      selectedProjectId: project?.id || null 
    }),
    
    setSelectedProjectId: (projectId) => set({ 
      selectedProjectId: projectId,
      selectedProject: get().projects.find(p => p.id === projectId) || null
    }),
    
    loadProjects: async (userId) => {
      if (!userId) return;
      
      set(state => ({ 
        loading: { ...state.loading, projects: true },
        errors: { ...state.errors, projects: null }
      }));
      
      try {
        const { data, error } = await projectService.getUserProjects(userId);
        if (error) throw error;
        
        set({ 
          projects: data || [],
          loading: { ...get().loading, projects: false }
        });
      } catch (error) {
        console.error('Error loading projects:', error);
        set({ 
          loading: { ...get().loading, projects: false },
          errors: { ...get().errors, projects: error.message }
        });
      }
    },
    
    createProject: async (projectData) => {
      set(state => ({ 
        loading: { ...state.loading, projects: true }
      }));
      
      try {
        const { data, error } = await projectService.createProject(projectData);
        if (error) throw error;
        
        // Add new project to list
        set(state => ({
          projects: [...state.projects, data],
          loading: { ...state.loading, projects: false }
        }));
        
        return { success: true, data };
      } catch (error) {
        console.error('Error creating project:', error);
        set(state => ({
          loading: { ...state.loading, projects: false },
          errors: { ...state.errors, projects: error.message }
        }));
        return { success: false, error: error.message };
      }
    },

    // =============================================
    // ITEMS MANAGEMENT ACTIONS
    // =============================================
    
    setItems: (items) => set({ items }),
    setTreeData: (treeData) => set({ treeData }),
    setCurrentFolder: (folder) => set({ currentFolder: folder }),
    
    loadItems: async (projectId) => {
      if (!projectId) return;
      
      set(state => ({ 
        loading: { ...state.loading, items: true },
        errors: { ...state.errors, items: null }
      }));
      
      try {
        const { data, error } = await supabase
          .from('dataroom_items')
          .select('*')
          .eq('project_id', projectId)
          .order('name');
          
        if (error) throw error;
        
        set({ 
          items: data || [],
          loading: { ...get().loading, items: false }
        });
      } catch (error) {
        console.error('Error loading items:', error);
        set({ 
          loading: { ...get().loading, items: false },
          errors: { ...get().errors, items: error.message }
        });
      }
    },

    // =============================================
    // USER MANAGEMENT ACTIONS
    // =============================================
    
    setUsers: (users) => set({ users }),
    
    loadUsers: async (projectId) => {
      if (!projectId) return;
      
      set(state => ({ 
        loading: { ...state.loading, users: true },
        errors: { ...state.errors, users: null }
      }));
      
      try {
        const { data, error } = await permissionService.getProjectUsers(projectId);
        if (error) throw error;
        
        set({ 
          users: data || [],
          loading: { ...get().loading, users: false }
        });
      } catch (error) {
        console.error('Error loading users:', error);
        set({ 
          loading: { ...get().loading, users: false },
          errors: { ...get().errors, users: error.message }
        });
      }
    },
    
    loadWizardUsers: async (projectId = null) => {
      set(state => ({ 
        loading: { ...state.loading, users: true },
        errors: { ...state.errors, users: null }
      }));
      
      try {
        let data, error;
        
        if (projectId) {
          // Load wizard users for specific project
          const result = await wizardUsersService.getWizardUsers(projectId);
          data = result.data;
          error = result.error;
        } else {
          // Load all wizard users across all projects
          const result = await wizardUsersService.getAllWizardUsers();
          data = result.data;
          error = result.error;
        }
        
        if (error) throw error;
        
        set({ 
          users: data || [],
          loading: { ...get().loading, users: false }
        });
      } catch (error) {
        console.error('Error loading wizard users:', error);
        set({ 
          loading: { ...get().loading, users: false },
          errors: { ...get().errors, users: error.message }
        });
      }
    },

    // =============================================
    // PARTY & TEAM MANAGEMENT ACTIONS
    // =============================================
    
    setParties: (parties) => set({ parties }),
    
    setTeams: (teams) => set({ teams }),
    
    setExpandedParties: (expandedParties) => set({ expandedParties }),
    
    togglePartyExpansion: (partyId) => {
      set(state => ({
        expandedParties: {
          ...state.expandedParties,
          [partyId]: !state.expandedParties[partyId]
        }
      }));
    },
    
    loadParties: async (projectId) => {
      set(state => ({ 
        loading: { ...state.loading, parties: true },
        errors: { ...state.errors, parties: null }
      }));
      
      try {
        const { data, error } = await partyTeamService.getParties(projectId);
        if (error) throw error;
        
        set({ 
          parties: data || [],
          loading: { ...get().loading, parties: false }
        });
      } catch (error) {
        console.error('Error loading parties:', error);
        set({ 
          loading: { ...get().loading, parties: false },
          errors: { ...get().errors, parties: error.message }
        });
      }
    },
    
    loadAllParties: async () => {
      set(state => ({ 
        loading: { ...state.loading, parties: true },
        errors: { ...state.errors, parties: null }
      }));
      
      try {
        const { data, error } = await partyTeamService.getAllParties();
        if (error) throw error;
        
        set({ 
          parties: data || [],
          loading: { ...get().loading, parties: false }
        });
      } catch (error) {
        console.error('Error loading all parties:', error);
        set({ 
          loading: { ...get().loading, parties: false },
          errors: { ...get().errors, parties: error.message }
        });
      }
    },
    
    loadTeams: async (partyId) => {
      set(state => ({ 
        loading: { ...state.loading, teams: true },
        errors: { ...state.errors, teams: null }
      }));
      
      try {
        const { data, error } = await partyTeamService.getTeams(partyId);
        if (error) throw error;
        
        set(state => ({ 
          teams: { ...state.teams, [partyId]: data || [] },
          loading: { ...state.loading, teams: false }
        }));
      } catch (error) {
        console.error('Error loading teams:', error);
        set({ 
          loading: { ...get().loading, teams: false },
          errors: { ...get().errors, teams: error.message }
        });
      }
    },
    
    loadAllTeams: async () => {
      set(state => ({ 
        loading: { ...state.loading, teams: true },
        errors: { ...state.errors, teams: null }
      }));
      
      try {
        const { data, error } = await partyTeamService.getAllTeams();
        if (error) throw error;
        
        // Transform data to group by party
        const teamsByParty = {};
        data?.forEach(team => {
          if (!teamsByParty[team.party_id]) {
            teamsByParty[team.party_id] = [];
          }
          teamsByParty[team.party_id].push(team);
        });
        
        set({ 
          teams: teamsByParty,
          loading: { ...get().loading, teams: false }
        });
      } catch (error) {
        console.error('Error loading all teams:', error);
        set({ 
          loading: { ...get().loading, teams: false },
          errors: { ...get().errors, teams: error.message }
        });
      }
    },
    
    createParty: async (projectId, partyData) => {
      set(state => ({ 
        loading: { ...state.loading, parties: true }
      }));
      
      try {
        const { data, error } = await partyTeamService.createParty(projectId, partyData);
        if (error) throw error;
        
        // Add new party to list
        set(state => ({
          parties: [...state.parties, data],
          loading: { ...state.loading, parties: false }
        }));
        
        return { success: true, data };
      } catch (error) {
        console.error('Error creating party:', error);
        set(state => ({
          loading: { ...state.loading, parties: false },
          errors: { ...state.errors, parties: error.message }
        }));
        return { success: false, error: error.message };
      }
    },
    
    createTeam: async (partyId, teamData) => {
      set(state => ({ 
        loading: { ...state.loading, teams: true }
      }));
      
      try {
        const { data, error } = await partyTeamService.createTeam(partyId, teamData);
        if (error) throw error;
        
        // Add new team to party
        set(state => ({
          teams: {
            ...state.teams,
            [partyId]: [...(state.teams[partyId] || []), data]
          },
          loading: { ...state.loading, teams: false }
        }));
        
        return { success: true, data };
      } catch (error) {
        console.error('Error creating team:', error);
        set(state => ({
          loading: { ...state.loading, teams: false },
          errors: { ...state.errors, teams: error.message }
        }));
        return { success: false, error: error.message };
      }
    },

    // =============================================
    // MODAL MANAGEMENT ACTIONS
    // =============================================
    
    openModal: (modalName, data = null) => {
      set(state => ({
        modals: {
          ...state.modals,
          [modalName]: data !== null ? data : true
        }
      }));
    },
    
    closeModal: (modalName) => {
      set(state => ({
        modals: {
          ...state.modals,
          [modalName]: modalName.match(/Modal|Project|show/) ? false : null
        }
      }));
    },
    
    setModals: (modals) => set({ modals }),

    // =============================================
    // UI STATE ACTIONS
    // =============================================
    
    setBreadcrumb: (breadcrumb) => set({ breadcrumb }),
    
    setSearchTerm: (searchTerm) => set({ searchTerm }),
    
    addMessage: (message, type = 'info') => {
      const id = Date.now();
      const newMessage = { id, message, type, timestamp: new Date() };
      
      set(state => ({
        messages: [...state.messages, newMessage]
      }));
      
      // Auto-remove after 5 seconds
      setTimeout(() => {
        set(state => ({
          messages: state.messages.filter(m => m.id !== id)
        }));
      }, 5000);
    },
    
    clearMessages: () => set({ messages: [] }),

    // =============================================
    // LOADING & ERROR STATE ACTIONS
    // =============================================
    
    setLoading: (key, loading) => {
      set(state => ({
        loading: { ...state.loading, [key]: loading }
      }));
    },
    
    setError: (key, error) => {
      set(state => ({
        errors: { ...state.errors, [key]: error }
      }));
    },
    
    clearError: (key) => {
      set(state => ({
        errors: { ...state.errors, [key]: null }
      }));
    },
    
    clearAllErrors: () => {
      set({
        errors: {
          projects: null,
          items: null,
          users: null,
          parties: null,
          teams: null,
          global: null
        }
      });
    },

    // =============================================
    // UTILITY ACTIONS
    // =============================================
    
    reset: () => {
      set({
        projects: [],
        selectedProject: null,
        selectedProjectId: null,
        items: [],
        treeData: [],
        currentFolder: null,
        users: [],
        userRoles: {},
        parties: [],
        teams: {},
        expandedParties: {},
        modals: {},
        breadcrumb: [],
        searchTerm: '',
        messages: [],
        loading: {
          projects: false,
          items: false,
          users: false,
          parties: false,
          teams: false,
          global: false
        },
        errors: {
          projects: null,
          items: null,
          users: null,
          parties: null,
          teams: null,
          global: null
        }
      });
    }
  }))
);

export default useDataroomStore;
